import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  User,
  Users,
  Search,
  X,
  Loader2,
  Database,
  ShieldAlert,
  ShieldCheck,
  Server,
  Layers,
  UserX,
  UserCheck,
  Info,
  Tag,
  HardDrive,
  Key,
  Settings2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  Copy,
  Check,
} from 'lucide-react';
import baseapi from '../../../services/baseapi';

interface CustomSelectOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

interface CustomSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: CustomSelectOption[];
  icon?: React.ReactNode;
  minWidth?: string;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  value,
  onChange,
  options,
  icon,
  minWidth = '135px',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const updateCoords = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: Math.max(rect.width, 150),
      });
    }
  }, []);

  const toggleOpen = () => {
    if (!isOpen) {
      updateCoords();
    }
    setIsOpen((prev) => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    const handleScrollOrResize = () => {
      if (isOpen) {
        updateCoords();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('resize', handleScrollOrResize);
      window.addEventListener('scroll', handleScrollOrResize, true);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handleScrollOrResize);
      window.removeEventListener('scroll', handleScrollOrResize, true);
    };
  }, [isOpen, updateCoords]);

  const selectedOption = options.find((opt) => opt.value === value) || options[0];

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={toggleOpen}
        style={{ minWidth }}
        className="flex items-center justify-between gap-2 bg-gray-800/90 hover:bg-gray-700/80 border border-gray-700 rounded-lg px-2.5 py-1.5 text-xs text-gray-200 font-medium focus:outline-none focus:border-orange-500 transition-colors cursor-pointer shadow-sm h-8"
      >
        <div className="flex items-center gap-1.5 truncate min-w-0">
          {selectedOption?.icon || icon}
          <span className="truncate">{selectedOption?.label || value}</span>
        </div>
        <ChevronDown className={`w-3.5 h-3.5 text-gray-400 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: 'absolute',
              top: `${coords.top}px`,
              left: `${coords.left}px`,
              minWidth: `${coords.width}px`,
              zIndex: 99999,
            }}
            className="bg-gray-900 border border-gray-700 rounded-lg shadow-2xl overflow-hidden py-1 max-h-60 overflow-y-auto custom-scroll animate-in fade-in slide-in-from-top-1 duration-150"
          >
            {options.map((opt) => {
              const isSelected = opt.value === value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    onChange(opt.value);
                    setIsOpen(false);
                  }}
                  className={`w-full text-left px-3 py-1.5 text-xs font-semibold flex items-center justify-between gap-2 cursor-pointer transition-colors ${isSelected
                    ? 'bg-orange-500/15 text-orange-400 font-bold'
                    : 'text-gray-200 hover:bg-gray-800 hover:text-white'
                    }`}
                >
                  <div className="flex items-center gap-2 truncate min-w-0">
                    {opt.icon}
                    <span className="truncate">{opt.label}</span>
                  </div>
                  {isSelected && <Check className="w-3.5 h-3.5 text-orange-400 shrink-0" />}
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </>
  );
};

interface UsersLoginsViewProps {
  activeCluster: string;
  clientId?: string;
  refreshKey?: number;
  dbType?: string;
}

export interface PrincipalItem {
  agent_id?: number | string;
  asset_id?: number | string;
  auth_type?: string;
  client_id?: number | string;
  cluster_id?: string;
  collected_at?: string;
  config_id?: string;
  created_at?: string;
  database?: string;
  db_type?: string;
  default_database?: string;
  hostname?: string;
  indexed_at?: string;
  ip?: string;
  is_disabled?: boolean;
  is_system?: boolean;
  log_timestamp?: string;
  login_name?: string;
  master_agent_id?: string;
  modified_at?: string;
  name: string;
  organization_id?: number | string;
  os?: string;
  roles?: string;
  scope?: string;
  type: string;
}

export interface UsersSummary {
  total_principals?: number;
  total_databases?: number;
  databases?: string[];
  server_scope_count?: number;
  database_scope_count?: number;
  system_count?: number;
  disabled_count?: number;
}

export const UsersLoginsView: React.FC<UsersLoginsViewProps> = ({
  activeCluster,
  clientId,
  refreshKey,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<PrincipalItem[]>([]);
  const [summary, setSummary] = useState<UsersSummary | null>(null);

  // Filters & Search
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedScope, setSelectedScope] = useState<string>('all');
  const [selectedDatabase, setSelectedDatabase] = useState<string>('all');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [originFilter, setOriginFilter] = useState<'all' | 'system' | 'custom'>('all');

  // Sorting
  const [sortBy, setSortBy] = useState<keyof PrincipalItem>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Pagination
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(25);

  // Selected item for drawer
  const [selectedPrincipal, setSelectedPrincipal] = useState<PrincipalItem | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!activeCluster) return;
    setLoading(true);

    const params: any = {
      cluster_id: activeCluster,
      clusted_id: activeCluster,
    };
    if (clientId) params.client_id = clientId;

    try {
      const res = await baseapi.get('/db_monitor_dashboard/users', { params });
      if (res.data?.status || res.data?.data) {
        const rawList = res.data?.data || (Array.isArray(res.data) ? res.data : []);
        const list: PrincipalItem[] = Array.isArray(rawList) ? rawList : [];
        setData(list);
        setSummary(res.data?.summary || null);
      } else {
        setData([]);
        setSummary(null);
      }
    } catch (err) {
      console.error('UsersLoginsView fetch error:', err);
      setData([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [activeCluster, clientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  // Handle Copy
  const handleCopy = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1800);
  };

  // Computed summary values fallback
  const calculatedSummary = useMemo(() => {
    const totalPrincipals = summary?.total_principals ?? data.length;
    const allDbs = new Set<string>();
    let serverScope = 0;
    let dbScope = 0;
    let system = 0;
    let disabled = 0;

    data.forEach((p) => {
      if (p.database && p.database !== 'ALL') allDbs.add(p.database);
      if (p.scope?.toUpperCase() === 'SERVER') serverScope++;
      else if (p.scope?.toUpperCase() === 'DATABASE') dbScope++;

      if (p.is_system) system++;
      if (p.is_disabled) disabled++;
    });

    const dbsList = summary?.databases && summary.databases.length > 0
      ? summary.databases
      : Array.from(allDbs).sort();

    return {
      total_principals: summary?.total_principals ?? totalPrincipals,
      total_databases: summary?.total_databases ?? dbsList.length,
      databases: dbsList,
      server_scope_count: summary?.server_scope_count ?? serverScope,
      database_scope_count: summary?.database_scope_count ?? dbScope,
      system_count: summary?.system_count ?? system,
      disabled_count: summary?.disabled_count ?? disabled,
    };
  }, [summary, data]);

  // Unique list of types and scopes for dropdowns
  const availableTypes = useMemo(() => {
    const types = new Set<string>();
    data.forEach((p) => {
      if (p.type) types.add(p.type);
    });
    return Array.from(types).sort();
  }, [data]);

  // Database counts mapping from loaded records
  const databasePrincipalCounts = useMemo(() => {
    const map: Record<string, number> = {};
    calculatedSummary.databases.forEach((db) => {
      map[db] = 0;
    });
    data.forEach((p) => {
      if (p.database && p.database !== 'ALL') {
        map[p.database] = (map[p.database] || 0) + 1;
      }
    });
    return map;
  }, [calculatedSummary.databases, data]);

  // Distribution breakdowns
  const typeDistribution = useMemo(() => {
    const counts: Record<string, number> = {};
    data.forEach((p) => {
      const typeKey = p.type || 'UNKNOWN';
      counts[typeKey] = (counts[typeKey] || 0) + 1;
    });

    return Object.entries(counts)
      .map(([type, count]) => ({
        type,
        count,
        percentage: data.length > 0 ? (count / data.length) * 100 : 0,
      }))
      .sort((a, b) => b.count - a.count);
  }, [data]);

  const originDistribution = useMemo(() => {
    let system = 0;
    let custom = 0;
    data.forEach((p) => {
      if (p.is_system) system++;
      else custom++;
    });
    const total = data.length || 1;
    return {
      system,
      custom,
      systemPercent: (system / total) * 100,
      customPercent: (custom / total) * 100,
    };
  }, [data]);

  // Filtered & Sorted Data
  const filteredData = useMemo(() => {
    return data.filter((item) => {
      // Search
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        const matchName = item.name?.toLowerCase().includes(query);
        const matchLogin = item.login_name?.toLowerCase().includes(query);
        const matchDb = item.database?.toLowerCase().includes(query);
        const matchType = item.type?.toLowerCase().includes(query);
        const matchRoles = item.roles?.toLowerCase().includes(query);
        if (!matchName && !matchLogin && !matchDb && !matchType && !matchRoles) {
          return false;
        }
      }

      // Scope
      if (selectedScope !== 'all') {
        if ((item.scope || '').toUpperCase() !== selectedScope.toUpperCase()) {
          return false;
        }
      }

      // Database
      if (selectedDatabase !== 'all') {
        if (item.database !== selectedDatabase) {
          return false;
        }
      }

      // Type
      if (selectedType !== 'all') {
        if (item.type !== selectedType) {
          return false;
        }
      }

      // Origin
      if (originFilter === 'system' && !item.is_system) return false;
      if (originFilter === 'custom' && item.is_system) return false;

      return true;
    }).sort((a, b) => {
      let aVal: any = a[sortBy] ?? '';
      let bVal: any = b[sortBy] ?? '';

      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, searchQuery, selectedScope, selectedDatabase, selectedType, originFilter, sortBy, sortOrder]);

  // Paginated Data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredData.slice(startIndex, startIndex + pageSize);
  }, [filteredData, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredData.length / pageSize) || 1;

  const handleSort = (field: keyof PrincipalItem) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const SortIcon = ({ field }: { field: keyof PrincipalItem }) => {
    if (sortBy !== field) {
      return <ArrowUpDown className="w-3 h-3 text-gray-200 opacity-60 ml-1 inline-block" />;
    }
    return sortOrder === 'asc' ? (
      <ArrowUp className="w-3 h-3 text-orange-400 ml-1 inline-block" />
    ) : (
      <ArrowDown className="w-3 h-3 text-orange-400 ml-1 inline-block" />
    );
  };

  // Helper for Type formatting
  const formatTypeName = (type: string) => {
    if (!type) return '-';
    return type.replace(/_/g, ' ');
  };

  // Helper for Type Badge styling
  const getTypeBadgeStyle = (type: string) => {
    switch (type) {
      case 'SQL_LOGIN':
        return ' text-emerald-400 ';
      case 'SQL_USER':
        return ' text-blue-400 ';
      case 'DATABASE_ROLE':
        return ' text-purple-400 ';
      case 'WINDOWS_LOGIN':
      case 'WINDOWS_GROUP':
        return ' text-amber-400 ';
      case 'CERTIFICATE_MAPPED_USER':
        return ' text-cyan-400 ';
      default:
        return ' text-gray-200 ';
    }
  };

  // Format timestamp helper
  const formatTimestamp = (ts?: string) => {
    if (!ts) return '-';
    try {
      const d = new Date(ts);
      if (isNaN(d.getTime())) return ts;
      return d.toISOString().replace('T', ' ').replace('Z', ' UTC').substring(0, 19) + ' UTC';
    } catch {
      return ts;
    }
  };

  // Custom Dropdown Options
  const scopeOptions: CustomSelectOption[] = useMemo(() => [
    { value: 'all', label: 'All Scopes', icon: <Layers className="w-3.5 h-3.5 text-gray-400" /> },
    { value: 'SERVER', label: 'Server', icon: <Server className="w-3.5 h-3.5 text-purple-400" /> },
    { value: 'DATABASE', label: 'Database', icon: <Layers className="w-3.5 h-3.5 text-blue-400" /> },
  ], []);

  const databaseOptions: CustomSelectOption[] = useMemo(() => {
    const list: CustomSelectOption[] = [
      { value: 'all', label: 'All Databases', icon: <Database className="w-3.5 h-3.5 text-gray-400" /> },
    ];
    calculatedSummary.databases.forEach((db) => {
      list.push({
        value: db,
        label: db,
        icon: <Database className="w-3.5 h-3.5 text-blue-400" />,
      });
    });
    if (!calculatedSummary.databases.includes('ALL')) {
      list.push({
        value: 'ALL',
        label: 'ALL',
        icon: <Database className="w-3.5 h-3.5 text-purple-400" />,
      });
    }
    return list;
  }, [calculatedSummary.databases]);

  const typeOptions: CustomSelectOption[] = useMemo(() => {
    const list: CustomSelectOption[] = [
      { value: 'all', label: 'All Types', icon: <Tag className="w-3.5 h-3.5 text-gray-400" /> },
    ];
    availableTypes.forEach((type) => {
      list.push({
        value: type,
        label: formatTypeName(type),
        icon: <Tag className="w-3.5 h-3.5 text-cyan-400" />,
      });
    });
    return list;
  }, [availableTypes]);

  // Context metadata from first item or default
  // const contextData = useMemo(() => {
  //   const item = data[0] || {};
  //   return {
  //     db_type: item.db_type || dbType || 'mssql',
  //     cluster: item.cluster_id || activeCluster,
  //     hostname: item.hostname || 'ip-172-26-10-24',
  //     config_id: item.config_id || 'mssql@localhost:1433',
  //     asset_id: item.asset_id || 1941768,
  //     agent_id: item.agent_id || 1133,
  //     client_id: item.client_id || clientId || 86,
  //     organization_id: item.organization_id || 2,
  //     last_collected: item.collected_at || item.log_timestamp,
  //   };
  // }, [data, dbType, activeCluster, clientId]);

  return (
    <div className="space-y-5 pb-16 animate-in fade-in duration-200">
      {/* Top Header Section */}
      {/* <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-gray-900/60 p-4 rounded-xl border border-gray-800 backdrop-blur-sm">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="text-xl font-bold text-white tracking-tight flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-orange-500" />
              Users &amp; Logins
            </h1>
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-orange-500/10 text-orange-400 border border-orange-500/30">
              {contextData.db_type.toUpperCase()}
            </span>
          </div>
          <p className="text-xs text-gray-200 mt-1">
            Database principals, server logins and roles across the SQL Server instance
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-200 hover:text-white text-xs font-semibold border border-gray-700 transition-all shadow-sm disabled:opacity-50"
            title="Refresh Users Data"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin text-orange-400' : ''}`} />
            Refresh
          </button>
        </div>
      </div> */}

      {loading && !data.length ? (
        <div className="flex flex-col items-center justify-center py-24 bg-gray-900/40 rounded-xl border border-gray-800">
          <Loader2 className="w-8 h-8 text-orange-500 animate-spin mb-3" />
          <p className="text-sm font-medium text-gray-200">Loading security principals...</p>
          <p className="text-xs text-gray-200 mt-1">Scanning server logins, database users, and roles</p>
        </div>
      ) : (
        <>
          {/* Top 8 KPI Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-2.5">
            {/* Total Principals */}
            <div className="bg-gray-900/80 rounded-xl border border-gray-800/80 p-3 shadow-sm hover:border-gray-700 transition-colors flex flex-col justify-between">
              <div className="flex items-center justify-between gap-1 text-gray-200">
                <div className="flex items-center gap-1.5 truncate min-w-0">
                  <Users className="w-3.5 h-3.5 text-orange-400 shrink-0" />
                  <span className="text-[10px] font-bold uppercase tracking-wider truncate">
                    Total
                  </span>
                </div>
                <div className="group/info relative inline-flex items-center">
                  <Info className="w-3 h-3 text-gray-400 hover:text-gray-200 cursor-help shrink-0 transition-colors" />
                  <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1.5 z-50 px-2 py-1 bg-gray-950/95 backdrop-blur-sm text-gray-200 text-[10px] font-medium rounded-md border border-gray-700 shadow-2xl whitespace-nowrap opacity-0 invisible group-hover/info:opacity-100 group-hover/info:visible transition-all duration-150 pointer-events-none">
                    All discovered principals
                  </div>
                </div>
              </div>
              <div className="mt-2">
                <span className="text-xl font-black text-white tracking-tight">
                  {calculatedSummary.total_principals}
                </span>
              </div>
            </div>

            {/* Databases */}
            <div className="bg-gray-900/80 rounded-xl border border-gray-800/80 p-3 shadow-sm hover:border-gray-700 transition-colors flex flex-col justify-between">
              <div className="flex items-center justify-between gap-1 text-gray-200">
                <div className="flex items-center gap-1.5 truncate min-w-0">
                  <Database className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                  <span className="text-[10px] font-bold uppercase tracking-wider truncate">
                    Databases
                  </span>
                </div>
                <div className="group/info relative inline-flex items-center">
                  <Info className="w-3 h-3 text-gray-400 hover:text-gray-200 cursor-help shrink-0 transition-colors" />
                  <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1.5 z-50 px-2 py-1 bg-gray-950/95 backdrop-blur-sm text-gray-200 text-[10px] font-medium rounded-md border border-gray-700 shadow-2xl whitespace-nowrap opacity-0 invisible group-hover/info:opacity-100 group-hover/info:visible transition-all duration-150 pointer-events-none">
                    Databases scanned
                  </div>
                </div>
              </div>
              <div className="mt-2">
                <span className="text-xl font-black text-white tracking-tight">
                  {calculatedSummary.total_databases}
                </span>
              </div>
            </div>

            {/* Server Scope */}
            <div
              onClick={() => {
                setSelectedScope(selectedScope === 'SERVER' ? 'all' : 'SERVER');
                setCurrentPage(1);
              }}
              className={`rounded-xl border p-3 shadow-sm transition-all cursor-pointer flex flex-col justify-between ${selectedScope === 'SERVER'
                  ? 'bg-purple-950/30 border-purple-500 ring-1 ring-purple-500/50'
                  : 'bg-gray-900/80 border-gray-800/80 hover:border-purple-500/50'
                }`}
            >
              <div className="flex items-center justify-between gap-1 text-gray-200">
                <div className="flex items-center gap-1.5 truncate min-w-0">
                  <Server className="w-3.5 h-3.5 text-purple-400 shrink-0" />
                  <span className="text-[10px] font-bold uppercase tracking-wider truncate">
                    Server Scope
                  </span>
                </div>
                <div className="group/info relative inline-flex items-center">
                  <Info className="w-3 h-3 text-gray-400 hover:text-gray-200 cursor-help shrink-0 transition-colors" />
                  <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1.5 z-50 px-2 py-1 bg-gray-950/95 backdrop-blur-sm text-gray-200 text-[10px] font-medium rounded-md border border-gray-700 shadow-2xl whitespace-nowrap opacity-0 invisible group-hover/info:opacity-100 group-hover/info:visible transition-all duration-150 pointer-events-none">
                    Server-level logins (SQL_LOGIN)
                  </div>
                </div>
              </div>
              <div className="mt-2">
                <span className="text-xl font-black text-purple-400 tracking-tight">
                  {calculatedSummary.server_scope_count}
                </span>
              </div>
            </div>

            {/* Database Scope */}
            <div
              onClick={() => {
                setSelectedScope(selectedScope === 'DATABASE' ? 'all' : 'DATABASE');
                setCurrentPage(1);
              }}
              className={`rounded-xl border p-3 shadow-sm transition-all cursor-pointer flex flex-col justify-between ${selectedScope === 'DATABASE'
                  ? 'bg-blue-950/30 border-blue-500 ring-1 ring-blue-500/50'
                  : 'bg-gray-900/80 border-gray-800/80 hover:border-blue-500/50'
                }`}
            >
              <div className="flex items-center justify-between gap-1 text-gray-200">
                <div className="flex items-center gap-1.5 truncate min-w-0">
                  <Layers className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                  <span className="text-[10px] font-bold uppercase tracking-wider truncate">
                    Database Scope
                  </span>
                </div>
                <div className="group/info relative inline-flex items-center">
                  <Info className="w-3 h-3 text-gray-400 hover:text-gray-200 cursor-help shrink-0 transition-colors" />
                  <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1.5 z-50 px-2 py-1 bg-gray-950/95 backdrop-blur-sm text-gray-200 text-[10px] font-medium rounded-md border border-gray-700 shadow-2xl whitespace-nowrap opacity-0 invisible group-hover/info:opacity-100 group-hover/info:visible transition-all duration-150 pointer-events-none">
                    Database users and roles
                  </div>
                </div>
              </div>
              <div className="mt-2">
                <span className="text-xl font-black text-blue-400 tracking-tight">
                  {calculatedSummary.database_scope_count}
                </span>
              </div>
            </div>

            {/* System */}
            <div
              onClick={() => {
                setOriginFilter(originFilter === 'system' ? 'all' : 'system');
                setCurrentPage(1);
              }}
              className={`rounded-xl border p-3 shadow-sm transition-all cursor-pointer flex flex-col justify-between ${originFilter === 'system'
                  ? 'bg-amber-950/30 border-amber-500 ring-1 ring-amber-500/50'
                  : 'bg-gray-900/80 border-gray-800/80 hover:border-amber-500/50'
                }`}
            >
              <div className="flex items-center justify-between gap-1 text-gray-200">
                <div className="flex items-center gap-1.5 truncate min-w-0">
                  <ShieldAlert className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                  <span className="text-[10px] font-bold uppercase tracking-wider truncate">
                    System
                  </span>
                </div>
                <div className="group/info relative inline-flex items-center">
                  <Info className="w-3 h-3 text-gray-400 hover:text-gray-200 cursor-help shrink-0 transition-colors" />
                  <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1.5 z-50 px-2 py-1 bg-gray-950/95 backdrop-blur-sm text-gray-200 text-[10px] font-medium rounded-md border border-gray-700 shadow-2xl whitespace-nowrap opacity-0 invisible group-hover/info:opacity-100 group-hover/info:visible transition-all duration-150 pointer-events-none">
                    System principals
                  </div>
                </div>
              </div>
              <div className="mt-2">
                <span className="text-xl font-black text-amber-400 tracking-tight">
                  {calculatedSummary.system_count}
                </span>
              </div>
            </div>

            {/* Custom */}
            <div
              onClick={() => {
                setOriginFilter(originFilter === 'custom' ? 'all' : 'custom');
                setCurrentPage(1);
              }}
              className={`rounded-xl border p-3 shadow-sm transition-all cursor-pointer flex flex-col justify-between ${originFilter === 'custom'
                  ? 'bg-emerald-950/30 border-emerald-500 ring-1 ring-emerald-500/50'
                  : 'bg-gray-900/80 border-gray-800/80 hover:border-emerald-500/50'
                }`}
            >
              <div className="flex items-center justify-between gap-1 text-gray-200">
                <div className="flex items-center gap-1.5 truncate min-w-0">
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                  <span className="text-[10px] font-bold uppercase tracking-wider truncate">
                    Custom
                  </span>
                </div>
                <div className="group/info relative inline-flex items-center">
                  <Info className="w-3 h-3 text-gray-400 hover:text-gray-200 cursor-help shrink-0 transition-colors" />
                  <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1.5 z-50 px-2 py-1 bg-gray-950/95 backdrop-blur-sm text-gray-200 text-[10px] font-medium rounded-md border border-gray-700 shadow-2xl whitespace-nowrap opacity-0 invisible group-hover/info:opacity-100 group-hover/info:visible transition-all duration-150 pointer-events-none">
                    User-created principals
                  </div>
                </div>
              </div>
              <div className="mt-2">
                <span className="text-xl font-black text-emerald-400 tracking-tight">
                  {originDistribution.custom}
                </span>
              </div>
            </div>

            {/* Active */}
            <div className="bg-gray-900/80 rounded-xl border border-gray-800/80 p-3 shadow-sm hover:border-gray-700 transition-colors flex flex-col justify-between">
              <div className="flex items-center justify-between gap-1 text-gray-200">
                <div className="flex items-center gap-1.5 truncate min-w-0">
                  <UserCheck className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                  <span className="text-[10px] font-bold uppercase tracking-wider truncate">
                    Active
                  </span>
                </div>
                <div className="group/info relative inline-flex items-center">
                  <Info className="w-3 h-3 text-gray-400 hover:text-gray-200 cursor-help shrink-0 transition-colors" />
                  <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1.5 z-50 px-2 py-1 bg-gray-950/95 backdrop-blur-sm text-gray-200 text-[10px] font-medium rounded-md border border-gray-700 shadow-2xl whitespace-nowrap opacity-0 invisible group-hover/info:opacity-100 group-hover/info:visible transition-all duration-150 pointer-events-none">
                    Active enabled accounts
                  </div>
                </div>
              </div>
              <div className="mt-2">
                <span className="text-xl font-black text-cyan-400 tracking-tight">
                  {Math.max(0, (calculatedSummary.total_principals ?? 0) - (calculatedSummary.disabled_count ?? 0))}
                </span>
              </div>
            </div>

            {/* Disabled */}
            <div className="bg-gray-900/80 rounded-xl border border-gray-800/80 p-3 shadow-sm hover:border-gray-700 transition-colors flex flex-col justify-between">
              <div className="flex items-center justify-between gap-1 text-gray-200">
                <div className="flex items-center gap-1.5 truncate min-w-0">
                  <UserX className="w-3.5 h-3.5 text-red-400 shrink-0" />
                  <span className="text-[10px] font-bold uppercase tracking-wider truncate">
                    Disabled
                  </span>
                </div>
                <div className="group/info relative inline-flex items-center">
                  <Info className="w-3 h-3 text-gray-400 hover:text-gray-200 cursor-help shrink-0 transition-colors" />
                  <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1.5 z-50 px-2 py-1 bg-gray-950/95 backdrop-blur-sm text-gray-200 text-[10px] font-medium rounded-md border border-gray-700 shadow-2xl whitespace-nowrap opacity-0 invisible group-hover/info:opacity-100 group-hover/info:visible transition-all duration-150 pointer-events-none">
                    Disabled accounts
                  </div>
                </div>
              </div>
              <div className="mt-2">
                <span className="text-xl font-black text-red-400 tracking-tight">
                  {calculatedSummary.disabled_count}
                </span>
              </div>
            </div>
          </div>

          {/* Database Coverage Grid */}
          <div className="bg-gray-900/80 rounded-xl border border-gray-800/80 p-4 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-xs font-bold text-gray-200 uppercase tracking-wider flex items-center gap-1.5">
                  <Database className="w-3.5 h-3.5 text-blue-400" />
                  Database Coverage
                </h2>
                <p className="text-[11px] text-gray-200">
                  Databases included in the security inventory
                </p>
              </div>
              <span className="text-[11px] font-medium text-gray-200">
                {calculatedSummary.databases.length} databases
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2.5">
              {calculatedSummary.databases.map((dbName) => {
                const count = databasePrincipalCounts[dbName] || 0;
                const isSelected = selectedDatabase === dbName;

                return (
                  <button
                    key={dbName}
                    onClick={() => {
                      setSelectedDatabase(isSelected ? 'all' : dbName);
                      setCurrentPage(1);
                    }}
                    className={`p-3 rounded-lg border text-left transition-all flex flex-col justify-between ${isSelected
                      ? 'bg-orange-500/10 border-orange-500/60 text-white ring-1 ring-orange-500/40'
                      : 'bg-gray-800/40 border-gray-700/60 hover:bg-gray-800/80 hover:border-gray-600'
                      }`}
                  >
                    <span className="text-xs font-bold text-white truncate max-w-full block" title={dbName}>
                      {dbName}
                    </span>
                    <div className="mt-2 flex items-baseline gap-1">
                      <span className={`text-lg font-black ${isSelected ? 'text-orange-400' : 'text-blue-400'}`}>
                        {count}
                      </span>
                      <span className="text-[10px] text-gray-200">principals</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Principal Inventory Main Table */}
          <div className="bg-gray-900/80 rounded-xl border border-gray-800/80 shadow-sm overflow-hidden">
            {/* Table Header & Controls */}
            <div className="p-4 border-b border-gray-800 space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <h2 className="text-xs font-bold text-gray-200 uppercase tracking-wider flex items-center gap-1.5">
                    <Users className="w-3.5 h-3.5 text-orange-400" />
                    Principal Inventory
                  </h2>
                  <p className="text-[11px] text-gray-200">
                    Server logins, database users and database roles
                  </p>
                </div>
                <div className="text-xs font-semibold text-gray-200">
                  {filteredData.length} {filteredData.length === 1 ? 'principal' : 'principals'}
                </div>
              </div>

              {/* Filter Bar */}
              <div className="flex flex-wrap items-center gap-2 pt-1">
                {/* Search input */}
                <div className="relative flex-1 min-w-[220px]">
                  <Search className="w-3.5 h-3.5 text-gray-200 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Search name, login, database or type..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full bg-gray-800/90 border border-gray-700 rounded-lg pl-8 pr-8 py-1.5 text-xs text-gray-100 placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-200 hover:text-white"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                {/* Scope Filter */}
                <CustomSelect
                  value={selectedScope}
                  onChange={(val) => {
                    setSelectedScope(val);
                    setCurrentPage(1);
                  }}
                  options={scopeOptions}
                  minWidth="125px"
                />

                {/* Database Filter */}
                <CustomSelect
                  value={selectedDatabase}
                  onChange={(val) => {
                    setSelectedDatabase(val);
                    setCurrentPage(1);
                  }}
                  options={databaseOptions}
                  minWidth="140px"
                />

                {/* Type Filter */}
                <CustomSelect
                  value={selectedType}
                  onChange={(val) => {
                    setSelectedType(val);
                    setCurrentPage(1);
                  }}
                  options={typeOptions}
                  minWidth="130px"
                />

                {/* Origin Segmented Toggle */}
                <div className="flex items-center bg-gray-800 p-0.5 rounded-lg border border-gray-700">
                  <button
                    onClick={() => {
                      setOriginFilter('all');
                      setCurrentPage(1);
                    }}
                    className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all ${originFilter === 'all'
                      ? 'bg-orange-500 text-white shadow-sm'
                      : 'text-gray-200 hover:text-white'
                      }`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => {
                      setOriginFilter('system');
                      setCurrentPage(1);
                    }}
                    className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all ${originFilter === 'system'
                      ? 'bg-amber-600 text-white shadow-sm'
                      : 'text-gray-200 hover:text-white'
                      }`}
                  >
                    System
                  </button>
                  <button
                    onClick={() => {
                      setOriginFilter('custom');
                      setCurrentPage(1);
                    }}
                    className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all ${originFilter === 'custom'
                      ? 'bg-emerald-600 text-white shadow-sm'
                      : 'text-gray-200 hover:text-white'
                      }`}
                  >
                    Custom
                  </button>
                </div>
              </div>
            </div>

            {/* Table Container */}
            <div className="overflow-x-auto custom-scroll">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="border-b border-gray-800 bg-gray-800/40 text-[11px] font-bold text-gray-200 uppercase tracking-wider select-none">
                    <th
                      onClick={() => handleSort('name')}
                      className="py-3 px-4 cursor-pointer hover:text-white transition-colors"
                    >
                      <div className="flex items-center gap-1.5">
                        <User className="w-3.5 h-3.5 text-orange-400/80" />
                        <span>NAME</span>
                        <SortIcon field="name" />
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('database')}
                      className="py-3 px-4 cursor-pointer hover:text-white transition-colors"
                    >
                      <div className="flex items-center gap-1.5">
                        <Database className="w-3.5 h-3.5 text-blue-400/80" />
                        <span>DATABASE</span>
                        <SortIcon field="database" />
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('scope')}
                      className="py-3 px-4 cursor-pointer hover:text-white transition-colors"
                    >
                      <div className="flex items-center gap-1.5">
                        <Server className="w-3.5 h-3.5 text-purple-400/80" />
                        <span>SCOPE</span>
                        <SortIcon field="scope" />
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('type')}
                      className="py-3 px-4 cursor-pointer hover:text-white transition-colors"
                    >
                      <div className="flex items-center gap-1.5">
                        <Tag className="w-3.5 h-3.5 text-cyan-400/80" />
                        <span>TYPE</span>
                        <SortIcon field="type" />
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('is_system')}
                      className="py-3 px-4 cursor-pointer hover:text-white transition-colors"
                    >
                      <div className="flex items-center gap-1.5">
                        <ShieldCheck className="w-3.5 h-3.5 text-emerald-400/80" />
                        <span>ORIGIN</span>
                        <SortIcon field="is_system" />
                      </div>
                    </th>
                    <th className="py-3 px-4">
                      <div className="flex items-center gap-1.5">
                        <HardDrive className="w-3.5 h-3.5 text-indigo-400/80" />
                        <span>DEFAULT DB</span>
                      </div>
                    </th>
                    <th className="py-3 px-4">
                      <div className="flex items-center gap-1.5">
                        <Key className="w-3.5 h-3.5 text-amber-400/80" />
                        <span>ROLES</span>
                      </div>
                    </th>
                    <th className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <Settings2 className="w-3.5 h-3.5 text-gray-400" />
                        <span>ACTION</span>
                      </div>
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-800/60">
                  {paginatedData.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="py-12 text-center text-gray-200">
                        <Users className="w-8 h-8 text-gray-200 mx-auto mb-2 opacity-50" />
                        <p className="text-sm font-medium text-gray-200">No principals match the filters</p>
                        <p className="text-xs text-gray-200 mt-0.5">Try resetting search or filters</p>
                      </td>
                    </tr>
                  ) : (
                    paginatedData.map((principal, idx) => {
                      const isSystem = principal.is_system;
                      const isDisabled = principal.is_disabled;
                      const scope = principal.scope?.toUpperCase() || 'DATABASE';

                      return (
                        <tr
                          key={`${principal.name}-${principal.database}-${principal.type}-${idx}`}
                          className="hover:bg-gray-800/40 transition-colors group"
                        >
                          {/* Name */}
                          <td className="py-3 px-4 font-semibold text-white">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-gray-100">{principal.name}</span>
                              {isDisabled && (
                                <span className="text-[10px] font-bold px-1.5 py-0.2 rounded bg-red-500/15 text-red-400 border border-red-500/30">
                                  Disabled
                                </span>
                              )}
                            </div>
                            {principal.login_name && principal.login_name !== principal.name && (
                              <div className="text-[10px] text-gray-200 font-mono">
                                Login: {principal.login_name}
                              </div>
                            )}
                          </td>

                          {/* Database */}
                          <td className="py-3 px-4">
                            <span
                              className={`text-xs font-mono font-medium ${principal.database === 'ALL'
                                ? 'text-purple-400'
                                : 'text-blue-400 hover:underline cursor-pointer'
                                }`}
                              onClick={() => {
                                if (principal.database && principal.database !== 'ALL') {
                                  setSelectedDatabase(principal.database);
                                  setCurrentPage(1);
                                }
                              }}
                            >
                              {principal.database || '-'}
                            </span>
                          </td>

                          {/* Scope */}
                          <td className="py-3 px-4">
                            <span
                              className={`text-[10px] font-bold px-2 py-0.5 rounded border ${scope === 'SERVER'
                                ? 'bg-purple-500/10 text-purple-400 border-purple-500/30'
                                : 'bg-blue-500/10 text-blue-400 border-blue-500/30'
                                }`}
                            >
                              {scope}
                            </span>
                          </td>

                          {/* Type */}
                          <td className="py-3 px-4">
                            <span
                              className={`text-[10px] font-bold px-2 py-0.5 ${getTypeBadgeStyle(
                                principal.type
                              )}`}
                            >
                              {principal.type?.replace(/_/g, " ")}
                            </span>
                          </td>

                          {/* Origin */}
                          <td className="py-3 px-4">
                            <span
                              className={`text-[10px] font-bold px-2 py-0.5 rounded border ${isSystem
                                ? 'bg-amber-500/10 text-amber-400 border-amber-500/30'
                                : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'
                                }`}
                            >
                              {isSystem ? 'System' : 'Custom'}
                            </span>
                          </td>

                          {/* Default DB */}
                          <td className="py-3 px-4 font-mono text-gray-200">
                            {principal.default_database || '-'}
                          </td>

                          {/* Roles */}
                          <td className="py-3 px-4">
                            {principal.roles ? (
                              <span
                                className="text-gray-200 font-mono text-[11px] truncate max-w-[200px] block"
                                title={principal.roles}
                              >
                                {principal.roles}
                              </span>
                            ) : (
                              <span className="text-gray-200">-</span>
                            )}
                          </td>

                          {/* Action */}
                          <td className="py-3 px-4 text-right">
                            <button
                              onClick={() => setSelectedPrincipal(principal)}
                              className="text-xs font-semibold text-blue-400 hover:text-blue-300 transition-colors inline-flex items-center gap-1 group-hover:underline"
                            >
                              View &rarr;
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Table Footer: Pagination & Metadata */}
            <div className="p-3.5 border-t border-gray-800 bg-gray-900/60 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-200">
              <div className="flex items-center gap-4">
                <span>
                  Showing{' '}
                  <strong className="text-gray-200">
                    {filteredData.length === 0 ? 0 : (currentPage - 1) * pageSize + 1}
                  </strong>{' '}
                  to{' '}
                  <strong className="text-gray-200">
                    {Math.min(currentPage * pageSize, filteredData.length)}
                  </strong>{' '}
                  of <strong className="text-gray-200">{filteredData.length}</strong> principals
                </span>

                {/* Page Size Selector */}
                <div className="flex items-center gap-1.5">
                  <span className="text-[11px] text-gray-200">Per page:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="bg-gray-800 border border-gray-700 rounded px-1.5 py-0.5 text-xs text-gray-200 font-medium focus:outline-none"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>

              {/* Pagination Controls */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-1 rounded bg-gray-800 border border-gray-700 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed text-gray-200"
                  title="Previous Page"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-xs font-medium text-gray-200 px-1">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-1 rounded bg-gray-800 border border-gray-700 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed text-gray-200"
                  title="Next Page"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Bottom Analytics Cards: Principal Type Distribution & System vs Custom */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Principal Type Distribution */}
            <div className="bg-gray-900/80 rounded-xl border border-gray-800/80 p-4 shadow-sm">
              <div className="mb-3">
                <h2 className="text-xs font-bold text-gray-200 uppercase tracking-wider flex items-center gap-1.5">
                  <Tag className="w-3.5 h-3.5 text-cyan-400" />
                  Principal Type Distribution
                </h2>
                <p className="text-[11px] text-gray-200">Breakdown from discovered records</p>
              </div>

              <div className="space-y-2.5">
                {typeDistribution.map((item) => {
                  let barColor = 'bg-blue-500';
                  if (item.type === 'SQL_LOGIN') barColor = 'bg-blue-500';
                  else if (item.type === 'SQL_USER') barColor = 'bg-blue-500';
                  else if (item.type === 'DATABASE_ROLE') barColor = 'bg-purple-500';
                  else if (item.type.includes('WINDOWS')) barColor = 'bg-amber-500';

                  return (
                    <div key={item.type} className="space-y-1">
                      <div className="flex items-center justify-between text-xs font-medium">
                        <span className="text-gray-200">{formatTypeName(item.type)}</span>
                        <span className="text-gray-200 font-mono text-[11px]">{item.count}</span>
                      </div>
                      <div className="w-full h-1.5 bg-gray-800 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${barColor} rounded-full transition-all duration-500`}
                          style={{ width: `${Math.max(item.percentage, 2)}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* System vs Custom */}
            <div className="bg-gray-900/80 rounded-xl border border-gray-800/80 p-4 shadow-sm">
              <div className="mb-3">
                <h2 className="text-xs font-bold text-gray-200 uppercase tracking-wider flex items-center gap-1.5">
                  <ShieldAlert className="w-3.5 h-3.5 text-amber-400" />
                  System vs Custom
                </h2>
                <p className="text-[11px] text-gray-200">Principal origin classification</p>
              </div>

              <div className="space-y-3 pt-2">
                {/* System */}
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-xs font-medium">
                    <span className="text-amber-400 flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-amber-500 inline-block" />
                      System
                    </span>
                    <span className="text-gray-200 font-mono text-[11px]">
                      {originDistribution.system} ({originDistribution.systemPercent.toFixed(1)}%)
                    </span>
                  </div>
                  <div className="w-full h-2 bg-gray-800 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-amber-500 rounded-full transition-all duration-500"
                      style={{ width: `${originDistribution.systemPercent}%` }}
                    />
                  </div>
                </div>

                {/* Custom */}
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-xs font-medium">
                    <span className="text-emerald-400 flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />
                      Custom
                    </span>
                    <span className="text-gray-200 font-mono text-[11px]">
                      {originDistribution.custom} ({originDistribution.customPercent.toFixed(1)}%)
                    </span>
                  </div>
                  <div className="w-full h-2 bg-gray-800 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                      style={{ width: `${originDistribution.customPercent}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Monitoring Context Footer Box */}
          {/* <div className="bg-gray-900/80 rounded-xl border border-gray-800/80 p-4 shadow-sm">
            <div className="mb-3">
              <h2 className="text-xs font-bold text-gray-200 uppercase tracking-wider">
                Monitoring Context
              </h2>
              <p className="text-[11px] text-gray-200">Collector and infrastructure information</p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="p-2.5 rounded-lg bg-gray-800/40 border border-gray-800">
                <span className="text-[10px] font-bold text-gray-200 uppercase tracking-wider block">
                  Database Type
                </span>
                <span className="text-xs font-semibold text-gray-200 mt-1 block">
                  {contextData.db_type === 'mssql' ? 'Microsoft SQL Server' : contextData.db_type}
                </span>
              </div>

              <div className="p-2.5 rounded-lg bg-gray-800/40 border border-gray-800">
                <span className="text-[10px] font-bold text-gray-200 uppercase tracking-wider block">
                  Cluster
                </span>
                <span className="text-xs font-mono font-semibold text-gray-200 mt-1 block truncate" title={contextData.cluster}>
                  {contextData.cluster}
                </span>
              </div>

              <div className="p-2.5 rounded-lg bg-gray-800/40 border border-gray-800">
                <span className="text-[10px] font-bold text-gray-200 uppercase tracking-wider block">
                  Hostname
                </span>
                <span className="text-xs font-mono font-semibold text-gray-200 mt-1 block truncate" title={contextData.hostname}>
                  {contextData.hostname}
                </span>
              </div>

              <div className="p-2.5 rounded-lg bg-gray-800/40 border border-gray-800">
                <span className="text-[10px] font-bold text-gray-200 uppercase tracking-wider block">
                  Configuration
                </span>
                <span className="text-xs font-mono font-semibold text-gray-200 mt-1 block truncate" title={contextData.config_id}>
                  {contextData.config_id}
                </span>
              </div>

              <div className="p-2.5 rounded-lg bg-gray-800/40 border border-gray-800">
                <span className="text-[10px] font-bold text-gray-200 uppercase tracking-wider block">
                  Asset ID
                </span>
                <span className="text-xs font-mono font-semibold text-gray-200 mt-1 block">
                  {contextData.asset_id}
                </span>
              </div>

              <div className="p-2.5 rounded-lg bg-gray-800/40 border border-gray-800">
                <span className="text-[10px] font-bold text-gray-200 uppercase tracking-wider block">
                  Agent ID
                </span>
                <span className="text-xs font-mono font-semibold text-gray-200 mt-1 block">
                  {contextData.agent_id}
                </span>
              </div>

              <div className="p-2.5 rounded-lg bg-gray-800/40 border border-gray-800">
                <span className="text-[10px] font-bold text-gray-200 uppercase tracking-wider block">
                  Client ID
                </span>
                <span className="text-xs font-mono font-semibold text-gray-200 mt-1 block">
                  {contextData.client_id}
                </span>
              </div>

              <div className="p-2.5 rounded-lg bg-gray-800/40 border border-gray-800">
                <span className="text-[10px] font-bold text-gray-200 uppercase tracking-wider block">
                  Organization ID
                </span>
                <span className="text-xs font-mono font-semibold text-gray-200 mt-1 block">
                  {contextData.organization_id}
                </span>
              </div>
            </div>

            {contextData.last_collected && (
              <div className="mt-3 text-right text-[11px] text-gray-200">
                Last collected: {formatTimestamp(contextData.last_collected)}
              </div>
            )}
          </div> */}
        </>
      )}

      {/* Details Slide-Over Drawer Modal */}
      {selectedPrincipal && (
        <div className="fixed inset-0 z-50 overflow-hidden bg-black/60 backdrop-blur-sm flex justify-end animate-in fade-in duration-200">
          <div
            className="w-full max-w-lg bg-gray-900 border-l border-gray-800 h-full flex flex-col shadow-2xl animate-in slide-in-from-right duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drawer Header */}
            <div className="p-4 border-b border-gray-800 flex items-start justify-between bg-gray-900/90">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-bold text-white font-mono">
                    {selectedPrincipal.name}
                  </h3>
                  <button
                    onClick={() => handleCopy(selectedPrincipal.name, 'header-name')}
                    className="p-1 text-gray-200 hover:text-white rounded"
                    title="Copy Name"
                  >
                    {copiedKey === 'header-name' ? (
                      <Check className="w-3.5 h-3.5 text-emerald-400" />
                    ) : (
                      <Copy className="w-3.5 h-3.5" />
                    )}
                  </button>
                </div>
                <div className="flex flex-wrap items-center gap-1.5 mt-2">
                  <span
                    className={`text-[10px] font-bold px-2 py-0.5 rounded border ${selectedPrincipal.scope === 'SERVER'
                      ? 'bg-purple-500/10 text-purple-400 border-purple-500/30'
                      : 'bg-blue-500/10 text-blue-400 border-blue-500/30'
                      }`}
                  >
                    {selectedPrincipal.scope}
                  </span>
                  <span
                    className={`text-[10px] font-bold px-2 py-0.5 rounded border ${getTypeBadgeStyle(
                      selectedPrincipal.type
                    )}`}
                  >
                    {selectedPrincipal.type}
                  </span>
                  <span
                    className={`text-[10px] font-bold px-2 py-0.5 rounded border ${selectedPrincipal.is_system
                      ? 'bg-amber-500/10 text-amber-400 border-amber-500/30'
                      : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'
                      }`}
                  >
                    {selectedPrincipal.is_system ? 'System' : 'Custom'}
                  </span>
                  {selectedPrincipal.is_disabled && (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-red-500/15 text-red-400 border border-red-500/30">
                      Disabled
                    </span>
                  )}
                </div>
              </div>

              <button
                onClick={() => setSelectedPrincipal(null)}
                className="p-1 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-200 hover:text-white border border-gray-700"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Drawer Body */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scroll">
              {/* Properties Grid */}
              <div className="bg-gray-800/40 rounded-xl border border-gray-800 p-3.5 space-y-3">
                <h4 className="text-[11px] font-bold text-gray-200 uppercase tracking-wider">
                  Principal Details
                </h4>

                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <span className="text-[10px] text-gray-200 block">Database</span>
                    <span className="font-mono text-gray-200 font-medium">
                      {selectedPrincipal.database || '-'}
                    </span>
                  </div>

                  <div>
                    <span className="text-[10px] text-gray-200 block">Login Name</span>
                    <span className="font-mono text-gray-200 font-medium truncate block">
                      {selectedPrincipal.login_name || '-'}
                    </span>
                  </div>

                  <div>
                    <span className="text-[10px] text-gray-200 block">Auth Type</span>
                    <span className="font-mono text-gray-200 font-medium">
                      {selectedPrincipal.auth_type || 'NONE'}
                    </span>
                  </div>

                  <div>
                    <span className="text-[10px] text-gray-200 block">Default Database</span>
                    <span className="font-mono text-gray-200 font-medium">
                      {selectedPrincipal.default_database || '-'}
                    </span>
                  </div>

                  <div className="col-span-2">
                    <span className="text-[10px] text-gray-200 block">Assigned Roles</span>
                    <span className="font-mono text-gray-200 font-medium break-all">
                      {selectedPrincipal.roles || 'None'}
                    </span>
                  </div>

                  <div>
                    <span className="text-[10px] text-gray-200 block">Created At</span>
                    <span className="font-mono text-gray-200 text-[11px]">
                      {formatTimestamp(selectedPrincipal.created_at)}
                    </span>
                  </div>

                  <div>
                    <span className="text-[10px] text-gray-200 block">Modified At</span>
                    <span className="font-mono text-gray-200 text-[11px]">
                      {formatTimestamp(selectedPrincipal.modified_at)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Infrastructure Context */}
              <div className="bg-gray-800/40 rounded-xl border border-gray-800 p-3.5 space-y-3">
                <h4 className="text-[11px] font-bold text-gray-200 uppercase tracking-wider">
                  Infrastructure &amp; Collector
                </h4>

                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <span className="text-[10px] text-gray-200 block">Hostname</span>
                    <span className="font-mono text-gray-200 truncate block">
                      {selectedPrincipal.hostname || '-'}
                    </span>
                  </div>

                  <div>
                    <span className="text-[10px] text-gray-200 block">IP Address</span>
                    <span className="font-mono text-gray-200">
                      {selectedPrincipal.ip || '-'}
                    </span>
                  </div>

                  <div>
                    <span className="text-[10px] text-gray-200 block">OS</span>
                    <span className="font-mono text-gray-200 uppercase">
                      {selectedPrincipal.os || '-'}
                    </span>
                  </div>

                  <div>
                    <span className="text-[10px] text-gray-200 block">Config ID</span>
                    <span className="font-mono text-gray-200 truncate block" title={selectedPrincipal.config_id}>
                      {selectedPrincipal.config_id || '-'}
                    </span>
                  </div>

                  <div>
                    <span className="text-[10px] text-gray-200 block">Agent ID</span>
                    <span className="font-mono text-gray-200">{selectedPrincipal.agent_id || '-'}</span>
                  </div>

                  <div>
                    <span className="text-[10px] text-gray-200 block">Asset ID</span>
                    <span className="font-mono text-gray-200">{selectedPrincipal.asset_id || '-'}</span>
                  </div>

                  <div>
                    <span className="text-[10px] text-gray-200 block">Client ID</span>
                    <span className="font-mono text-gray-200">{selectedPrincipal.client_id || '-'}</span>
                  </div>

                  <div>
                    <span className="text-[10px] text-gray-200 block">Organization ID</span>
                    <span className="font-mono text-gray-200">{selectedPrincipal.organization_id || '-'}</span>
                  </div>
                </div>
              </div>

              {/* Raw JSON viewer */}
              {/* <div className="bg-gray-800/40 rounded-xl border border-gray-800 p-3.5 space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="text-[11px] font-bold text-gray-200 uppercase tracking-wider flex items-center gap-1.5">
                    <Code className="w-3.5 h-3.5" /> Raw JSON Record
                  </h4>
                  <button
                    onClick={() =>
                      handleCopy(JSON.stringify(selectedPrincipal, null, 2), 'raw-json')
                    }
                    className="flex items-center gap-1 px-2 py-1 text-[10px] font-semibold bg-gray-800 hover:bg-gray-700 text-gray-200 hover:text-white rounded border border-gray-700 transition-colors"
                  >
                    {copiedKey === 'raw-json' ? (
                      <>
                        <Check className="w-3 h-3 text-emerald-400" /> Copied
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3" /> Copy JSON
                      </>
                    )}
                  </button>
                </div>
                <pre className="text-[10px] font-mono text-gray-200 bg-gray-950 p-3 rounded-lg overflow-x-auto max-h-56 custom-scroll border border-gray-800">
                  {JSON.stringify(selectedPrincipal, null, 2)}
                </pre>
              </div> */}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersLoginsView;
